import { useEffect, useState } from 'react';
import type { LiveOdds } from '@pons/contracts';
import type { ArenaRound } from './model';

export function useLiveOdds(
  round: Pick<ArenaRound, 'id' | 'odds'>,
  active: boolean,
) {
  const [amount, setAmount] = useState('100.00');
  const [remote, setRemote] = useState<{ id: string; quote: LiveOdds } | null>(
    null,
  );
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [reloading, setReloading] = useState(false);
  const hasOdds = !!round.odds,
    marketVersion = round.odds?.version,
    defaultAmount = round.odds?.amount;
  const normalized =
    /^(?:[1-9]\d{0,3})(?:\.\d{1,2})?$/.test(amount) && Number(amount) <= 1000
      ? Number(amount).toFixed(2)
      : null;
  const baseline = normalized === round.odds?.amount ? round.odds : null;
  const freshRemote =
    remote?.id === round.id &&
    remote.quote.amount === normalized &&
    remote.quote.version >= (round.odds?.version ?? 0)
      ? remote.quote
      : null;
  const quote = reloading
    ? null
    : freshRemote && (!baseline || freshRemote.version >= baseline.version)
      ? freshRemote
      : baseline;
  useEffect(() => {
    if (
      !active ||
      !hasOdds ||
      !normalized ||
      (normalized === defaultAmount && revision === 0)
    )
      return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      void fetch(
        (round.id.startsWith('game-') ? '/api/game/rounds/' : '/api/rounds/') +
          encodeURIComponent(round.id) +
          '/odds?amount=' +
          normalized,
        { signal: controller.signal, cache: 'no-store' },
      )
        .then(async (response) => {
          const body = await response.json();
          if (!response.ok) throw Error(body.error || 'Odds unavailable.');
          if (
            body.model !== 'lmsr-v1-floor2' ||
            body.unit !== 'virtual' ||
            body.amount !== normalized
          )
            throw Error('Invalid quote received.');
          if (!controller.signal.aborted) {
            setRemote({ id: round.id, quote: body });
            setReloading(false);
            setError('');
          }
        })
        .catch((error) => {
          if (!controller.signal.aborted)
            setError(
              error instanceof Error ? error.message : 'Odds unavailable.',
            );
        });
    }, 120);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [
    round.id,
    marketVersion,
    defaultAmount,
    hasOdds,
    normalized,
    active,
    revision,
  ]);
  return {
    amount,
    setAmount,
    quote,
    error: normalized ? error : 'Choose a size from 1 to 1000.',
    reload: () => {
      setError('');
      setReloading(true);
      setRemote(null);
      setRevision((n) => n + 1);
    },
  };
}
